

import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator"

import cloneDeep from "lodash/cloneDeep"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { BModal } from "bootstrap-vue"
import vSelect from "vue-select"

import DataModal from "./DataModal.vue"
import DataModalPage from "./DataModalPage.vue"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import { Getter, namespace } from "vuex-class"
import { Folder, FolderType } from "@/types/folder"
import { Team } from "@/types"
import { TenantFeatures } from "@/types/tenantFeatures"

const setup = namespace("setup")
@Component({
	components: {
		"help-icon": HelpIcon,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-modal": DataModal,
		"data-modal-page": DataModalPage,
		"folder-setup": () => import("./Folders/FolderSetup.vue")
	}
})
export default class DataTable extends Vue {
	$refs!: {
		dataModal: BModal
		dataModalPage: DataModalPage
	}
	@Prop()
	public dataType!: string;

	@Prop({ type: String }) helpText!: string;

	@Prop()
	public dataTypeHeader!: string;

	@Prop()
	public columns!: any[];

	@Prop()
	public items!: any[];

	@Prop({ type: String, default: "xl" })
	public modalSize!: string;

	@Prop({ type: Boolean })
	public isBusy!: boolean;

	@Prop({ type: Boolean, default: false })
	public overrideView!: boolean

	@Prop()
	public createNewItem!: (values: any) => any;

	@Prop()
	public deleteDetails!: (item: any) => any;

	@Prop({ default: true })
	public canCreateItem!: boolean;

	@Prop()
	public canDeleteItem!: (item: any) => boolean;

	@Prop()
	public canConfirmDelete!: (item: any) => boolean;

	@Prop()
	public loadItem!: (item: any) => any;

	@Prop({ default: false })
	public hide!: boolean

	@Prop({ default: true })
	public allowRowClick!: boolean

	@Prop({ default: false })
	public noLocalSorting!: boolean

	@Prop({ type: Boolean, default: false })
	public hideFilter!: boolean

	@Prop({ default: false })
	public modalPage!: boolean

	@Prop({ default: () => null })
	public titleColumnKey!: string | null

	@Prop({ default: null })
	public rowClass!: ((item: any, type: any) => string) | null;

	@Prop({ default: null })
	public filterFunction!: ((row: any, filter: string) => boolean) | null;

	@Prop()
	folderType!:FolderType

	@Prop()
	folderFeature!: boolean

	@Prop()
	folderList!: Folder []

	@Prop({ default: () => null })
	public deleteItemName!: ((item: any) => string) | null

	@Prop({ type: String, default: null })
	public cancelEditWarning!: string | null

	@Prop({ type: Boolean, default: true })
	public closeOnSave!: boolean

	@setup.Getter("getTeams")
	teams!: Team[];

	showManageFolder:boolean = false

	@Getter("getUseV2") getUseV2!: boolean

	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	tenantFeatures = TenantFeatures

	public filter: string = "";

	@Watch("filter")
	onFilterChanged (newValue: string) {
		this.$emit("filterChanged", newValue)
	}

	public deleting:boolean = false

	teamFolder = FolderType.Team

	/**
	 * Used to manually close the modal from parent component if CloseOnSave is set to false
	 */
	public closeModal () {
		if (this.modalPage) {
			this.$refs.dataModalPage.close()
		} else {
			this.$refs.dataModal.hide()
		}
	}

	public async rowClick (item: any) {
		if (!this.allowRowClick) {
			return
		}

		if (item.allowRowClick !== undefined && !item.allowRowClick) {
			return
		}

		if (item.onlyEmitOnRowClick !== undefined && item.onlyEmitOnRowClick) {
			this.$emit("row-opened", item)
			return
		}

		const itemClone = this.loadItem
			? await this.loadItem(item)
			: cloneDeep(item)

		if (this.modalPage) {
			this.$refs.dataModalPage.open(itemClone)
		} else {
			this.$refs.dataModal.open(itemClone)
		}

		this.$emit("row-opened", item)
	}

	public get tableColumns () {
		if (!this.columns) {
			return []
		}

		return this.columns.filter(c => c.inTable !== false)
	}

	public get tableColumnsKeys () {
		return this.tableColumns.map(c => c.key)
	}

	private get columnsWithTemplate (): any[] {
		if (!this.columns) { return [] }

		return this.columns.filter(c => c.hasTemplate)
	}

	private get boolColumns (): any[] {
		if (!this.columns) { return [] }

		return this.columns.filter(c => c.dataType === "checkbox" && !c.hasTemplate)
	}

	public createNew (values: any) {
		let newItem = this.createNewItem
			? this.createNewItem(values)
			: {}

		if (this.modalPage) {
			this.$refs.dataModalPage.open(newItem)
		} else {
			this.$refs.dataModal.open(newItem)
		}
	}

	@Emit("unsaved-item-change")
	public handleUnsavedItemChange (item: any) {
		return item
	}

	@Emit("save")
	public saveItem (item: any) {
		return item
	}

	@Emit("delete")
	public deleteItem (item: any) {
		this.deleting = false
		return item
	}

	columnTemplateName (column: any) {
		return "cell(" + column.key + ")"
	}

	@Emit("sortChanged")
	public sortChanged (context: any) {
		return context
	}

	public setDeleteStatus (deleting:any) {
		this.deleting = deleting
	}

	/**
	 * Gets the data type in the correct format for data-cy tags i.e.
	 * - Has all spaces replaced with '-'
	 * - Is all lowercase
	 * @private
	 */
	private get dataCyType () {
		let type = (this.dataTypeHeader ? this.dataTypeHeader : this.dataType)
		type = type.replace(/ /g, "-")
		return type.toLowerCase()
	}

	private get headerTitle () {
		return this.dataTypeHeader ? this.dataTypeHeader : this.dataType + "s"
	}

	private initFilter () {
		if (this.folderType !== this.teamFolder) {
			this.$emit("filterInitialized", true)
			this.$emit("refreshFolders")
		}
	}

	private itemRowClass (item: any, type: any) {
		if (this.rowClass) return this.rowClass(item, type)

		if (!item || type !== "row") return ""
		if (item.isFolderType) return "folder-row"

		return ""
	}
}

